import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gift, Send, Search, Plus, Minus, Trash2, Store, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import LoadingState from '@/components/shared/LoadingState';
import CustomerSelector from '@/components/orders/CustomerSelector';
import { api, ApiError } from '@/api/apiClient';
import { enqueue } from '@/lib/outbox';
import { notifyEnqueued } from '@/lib/syncEngine';
import { useOdooAuth } from '@/lib/OdooAuthContext';

const fmt2 = (n) =>
  Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function NewGift() {
  const navigate = useNavigate();
  const { odooSession } = useOdooAuth();
  const isLeader = odooSession?.role === 'manager' || odooSession?.role === 'supervisor';

  const [reps, setReps] = useState([]);
  const [repsLoading, setRepsLoading] = useState(true);
  const [rep, setRep] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [lines, setLines] = useState([]);   // {product_id, name, quantity, unit_value}
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // product search
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);

  const loadReps = useCallback(async () => {
    setRepsLoading(true);
    try {
      const res = await api.get('/sellgo_v5_gift_reps');
      const list = res?.success ? (res.data || []) : [];
      setReps(list);
      if (list.length === 1) setRep(list[0]);   // rep user → auto-pick self
    } catch { /* ignore */ }
    setRepsLoading(false);
  }, []);
  useEffect(() => { loadReps(); }, [loadReps]);

  const fetchProducts = useCallback(async (q) => {
    if (!rep) return;
    setSearching(true);
    try {
      const res = await api.get('/sellgo_inventory', {
        search: q || undefined,
        rep_uid: rep.user_id?.[0],
        limit: 30,
      });
      setResults(res?.success ? (res.data || []) : []);
    } catch { setResults([]); }
    setSearching(false);
  }, [rep]);

  useEffect(() => { if (rep) fetchProducts(''); }, [rep, fetchProducts]);

  const onSearch = (val) => {
    setQuery(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchProducts(val), 350);
  };

  const addProduct = (p) => {
    setLines((ls) => {
      const idx = ls.findIndex((l) => l.product_id === p.id);
      if (idx >= 0) {
        const copy = [...ls];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + 1 };
        return copy;
      }
      return [...ls, { product_id: p.id, name: p.name, quantity: 1, unit_value: p.lst_price || p.price || 0, stock: p.qty_available ?? p.qty ?? null }];
    });
  };
  const setQty = (pid, q) => setLines((ls) => ls.map((l) => l.product_id === pid ? { ...l, quantity: Math.max(0, q) } : l).filter((l) => l.quantity > 0));
  const bump = (pid, d) => setLines((ls) => ls.map((l) => l.product_id === pid ? { ...l, quantity: l.quantity + d } : l).filter((l) => l.quantity > 0));

  const totalValue = useMemo(
    () => lines.reduce((s, l) => s + l.quantity * (l.unit_value || 0), 0),
    [lines],
  );

  const remaining = rep?.remaining; // null = no cap
  const overCap = remaining != null && totalValue > remaining + 1e-6;

  const handleSubmit = async () => {
    if (!rep) { toast.error('اختر المندوب'); return; }
    if (!customer) { toast.error('اختر العميل'); return; }
    if (lines.length === 0) { toast.error('أضف صنفاً واحداً على الأقل'); return; }
    if (isLeader && overCap) { toast.error('قيمة الهدية تتجاوز السقف الشهري المتبقي للمندوب'); return; }
    setSubmitting(true);
    const body = {
      rep_user_id: rep.user_id?.[0],
      partner_id: customer.id,
      reason: reason.trim(),
      lines: lines.map((l) => ({ product_id: l.product_id, quantity: l.quantity })),
      dispense: isLeader,
    };

    // Offline: queue the gift and sync later.
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      await enqueue('gift', { body });
      notifyEnqueued();
      toast.success('تم حفظ الهدية — هتترفع تلقائياً لما النت يرجع');
      setSubmitting(false);
      navigate('/gifts');
      return;
    }

    try {
      const res = await api.post('/sellgo_v5_gift_create', body);
      if (res?.success && res.data) {
        toast.success(isLeader ? 'تم صرف الهدية' : 'تم إرسال طلب الهدية للاعتماد');
        navigate(`/gifts/${res.data.id}`, { replace: true });
      } else {
        toast.error(res?.error || 'تعذّر إنشاء الهدية');
      }
    } catch (e) {
      if (e instanceof ApiError && (!e.status || e.status === 0)) {
        await enqueue('gift', { body });
        notifyEnqueued();
        toast.success('تم حفظ الهدية — هتترفع تلقائياً لما النت يرجع');
        navigate('/gifts');
      } else {
        toast.error(e?.message || 'تعذّر إنشاء الهدية');
      }
    }
    setSubmitting(false);
  };

  if (repsLoading) return <LoadingState message="جاري التحميل..." />;

  return (
    <div className="max-w-2xl mx-auto pb-28" dir="rtl">
      <div className="px-4 pt-4 flex items-center gap-2 text-primary">
        <Gift className="w-5 h-5" />
        <h1 className="text-lg font-bold">{isLeader ? 'صرف هدية' : 'طلب هدية'}</h1>
      </div>

      {/* 1) Rep */}
      {reps.length > 1 && (
        <div className="px-4 mt-4">
          <p className="text-sm font-semibold text-foreground mb-2">المندوب (يُصرف من مخزنه)</p>
          <div className="grid grid-cols-1 gap-2">
            {reps.map((r) => {
              const active = rep?.user_id?.[0] === r.user_id?.[0];
              return (
                <button
                  key={r.user_id?.[0]}
                  onClick={() => { setRep(r); setLines([]); setResults([]); setQuery(''); }}
                  className={`text-right border rounded-2xl p-3 transition-colors ${active ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-muted'}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm">{r.user_id?.[1]}</span>
                    {r.warehouse && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Store className="w-3 h-3" /> {r.warehouse[1]}
                      </span>
                    )}
                  </div>
                  {r.monthly_limit > 0 && (
                    <p className="text-[11px] text-muted-foreground mt-1">
                      المتبقي من السقف الشهري: <span className="font-bold text-foreground">{fmt2(r.remaining)}</span>
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {rep && (
        <>
          {/* cap banner */}
          {rep.monthly_limit > 0 && (
            <div className="px-4 mt-3">
              <div className={`rounded-xl p-2.5 text-xs flex items-center gap-2 ${overCap ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-muted text-muted-foreground'}`}>
                {overCap && <AlertTriangle className="w-4 h-4 flex-shrink-0" />}
                السقف الشهري: {fmt2(rep.monthly_limit)} • المستخدم: {fmt2(rep.used_this_month)} • المتبقي: {fmt2(rep.remaining)}
              </div>
            </div>
          )}

          {/* 2) Customer */}
          <div className="px-4 mt-4">
            <p className="text-sm font-semibold text-foreground mb-2">العميل</p>
            {customer ? (
              <div className="bg-card border border-primary/50 rounded-2xl p-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-primary font-bold">{customer.name?.charAt(0) || '؟'}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground truncate">{customer.name}</p>
                    {(customer.phone || customer.mobile) && (
                      <p className="text-xs text-muted-foreground truncate">{customer.phone || customer.mobile}</p>
                    )}
                  </div>
                </div>
                <button onClick={() => setCustomer(null)} className="text-sm text-primary font-semibold flex-shrink-0">
                  تغيير
                </button>
              </div>
            ) : (
              <CustomerSelector
                selectedCustomer={null}
                customerInfo={null}
                onSelect={(c) => setCustomer(c)}
                onContinue={() => {}}
                scopeRepUid={rep?.user_id?.[0]}
              />
            )}
          </div>

          {/* 3) Products */}
          <div className="px-4 mt-4">
            <p className="text-sm font-semibold text-foreground mb-2">الأصناف</p>

            {lines.length > 0 && (
              <div className="space-y-2 mb-3">
                {lines.map((l) => (
                  <div key={l.product_id} className="bg-card border border-primary/40 rounded-2xl p-3 flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{l.name}</p>
                      <p className="text-[11px] text-muted-foreground">قيمة الوحدة: {fmt2(l.unit_value)}{l.stock != null ? ` • المتاح: ${fmt2(l.stock)}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => bump(l.product_id, -1)} className="w-8 h-8 rounded-lg border border-border flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
                      <input type="number" inputMode="decimal" min={0} step="any" value={l.quantity}
                        onChange={(e) => setQty(l.product_id, Number(e.target.value))}
                        className="w-14 text-center border border-border rounded-lg h-8 text-sm" />
                      <button onClick={() => bump(l.product_id, 1)} className="w-8 h-8 rounded-lg border border-border flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setQty(l.product_id, 0)} className="w-8 h-8 rounded-lg text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="relative">
              <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={query} onChange={(e) => onSearch(e.target.value)}
                placeholder="ابحث عن منتج لإضافته..."
                className="w-full border border-border rounded-xl h-11 pr-9 pl-3 text-sm focus:border-primary focus:outline-none"
              />
            </div>
            <div className="mt-2 space-y-1 max-h-72 overflow-y-auto">
              {searching && <p className="text-xs text-muted-foreground py-2 text-center">جاري البحث...</p>}
              {!searching && results.map((p) => {
                const stock = p.qty_available ?? p.qty ?? 0;
                return (
                  <button key={p.id} onClick={() => addProduct(p)}
                    className="w-full text-right flex items-center justify-between gap-2 p-2.5 rounded-xl hover:bg-muted border border-transparent hover:border-border">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm truncate">{p.name}</p>
                      <p className="text-[11px] text-muted-foreground">قيمة: {fmt2(p.price ?? p.list_price ?? p.lst_price ?? 0)}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${stock > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                        المتاح: {fmt2(stock)}
                      </span>
                      <Plus className="w-4 h-4 text-primary" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 4) Reason */}
          <div className="px-4 mt-4">
            <label className="text-sm font-semibold text-foreground">المناسبة / السبب (اختياري)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              placeholder="مثال: عميل مميز / مناسبة..."
              className="mt-1 w-full border border-border rounded-xl p-3 text-sm focus:border-primary focus:outline-none resize-none" />
          </div>

          {/* Sticky bar */}
          <div className="fixed bottom-0 right-0 left-0 bg-card border-t border-border px-4 py-3 z-30">
            <div className="max-w-2xl mx-auto flex items-center gap-3">
              <div className="flex-shrink-0">
                <p className="text-[11px] text-muted-foreground leading-none">قيمة الهدية</p>
                <p className={`text-base font-bold ${overCap ? 'text-red-600' : 'text-foreground'}`}>{fmt2(totalValue)}</p>
              </div>
              <Button onClick={handleSubmit} disabled={submitting || lines.length === 0 || !customer || (isLeader && overCap)}
                className="flex-1 bg-primary rounded-xl h-12 gap-2 font-semibold">
                <Send className="w-4 h-4" />
                {submitting ? 'جاري...' : isLeader ? 'صرف الهدية' : 'إرسال الطلب للاعتماد'}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
